import { type CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';

/** Sentinel value for "all publishers" — used in business logic, never displayed directly. */
export const PUBLISHER_ALL = '__ALL__';

/**
 * Builds a MongoDB-style locale filter for the assets query.
 *
 * @param locale - Locale code (e.g., "en_US") or empty string for no filter
 * @returns Filter object with `locale.$eq` or empty object
 */
export const buildLocaleFilter = (locale: string): Record<string, object> => {
  if (!locale) return {};
  return { locale: { $eq: locale } };
};

/**
 * Builds a MongoDB-style asset type filter for the assets query.
 *
 * @param assetType - "IMAGE", "VIDEO", or empty string for no filter
 * @returns Filter object with `assetType.$eq` or empty object
 */
export const buildAssetTypeFilter = (assetType: string): Record<string, object> => {
  if (!assetType) return {};
  return { assetType: { $eq: assetType } };
};

/**
 * Builds a MongoDB-style campaign IDs filter based on the selected country.
 * Finds all campaigns that have at least one product strategy targeting the given country,
 * then returns a filter constraining assets to those campaign IDs.
 *
 * @param country - Country code (e.g., "US") or empty string for no filter
 * @param campaigns - Array of campaign DTOs to search through
 * @returns Filter object with `campaignIds.$in` or empty object
 */
export const buildCountryFilter = (
  country: string,
  campaigns: CampaignDesiredStateDto[],
): Record<string, object> => {
  if (!country || campaigns.length === 0) return {};

  const countryCampaignIds = campaigns
    .filter(c => c.productStrategies.some(ps => ps.country === country))
    .map(c => c.campaignId);

  if (countryCampaignIds.length === 0) return { campaignIds: { $in: [] } };
  return { campaignIds: { $in: countryCampaignIds } };
};

/**
 * Derives sorted unique country codes from campaign product strategies.
 *
 * @param campaigns - Array of campaign DTOs
 * @returns Sorted array of unique country code strings
 */
export const deriveCountryOptions = (
  campaigns: CampaignDesiredStateDto[],
): string[] => {
  const countries = new Set<string>();
  campaigns.forEach(campaign => {
    campaign.productStrategies.forEach(ps => {
      if (ps.country) countries.add(ps.country);
    });
  });
  return Array.from(countries).sort();
};

/**
 * Filters campaign options by publisher. When the selected publisher is
 * PUBLISHER_ALL, all campaigns are returned unfiltered.
 *
 * @param campaigns - Array of campaign option objects with id and name
 * @param publisher - Selected publisher value
 * @param campaignsData - Full campaign DTOs used to look up publisher per campaign
 * @returns Filtered array of campaign options
 */
export const filterCampaignsByPublisher = (
  campaigns: Array<{ id: string; name: string }>,
  publisher: string,
  campaignsData: CampaignDesiredStateDto[] = [],
): Array<{ id: string; name: string }> => {
  if (publisher === PUBLISHER_ALL) return campaigns;

  return campaigns.filter(c =>
    campaignsData.find(camp => camp.campaignId === c.id)?.publisher === publisher,
  );
};
